import React, { useEffect } from 'react';
import Button from '../../BaseComponents/Button';
import { useFormikContext } from 'formik';
import { StepProps } from '../../../pages/complete-profile';
import {
  OnboardingFormValues
} from '../../../types/membersTypes';
import TextInput from '../../BaseComponents/TextInput';
import DatePickerComp from '../../BaseComponents/DatePicker';

type FemaleSexHormonesStepProps = {
  setCurrentStep: (step: StepProps) => void;
  saveStepData?: (stepNumber: number) => void;
  populateFormWithSavedData?: () => void;
  lastCompletedStep?: number;
  membershipType?: string | null;
};

const Step5: React.FC<FemaleSexHormonesStepProps> = ({
  setCurrentStep,
  saveStepData,
  populateFormWithSavedData
}) => {
  const { values, setFieldValue, validateForm } =
    useFormikContext<OnboardingFormValues>();

  const isFemale = values.gender?.key === 'female';

  const [hasPopulated, setHasPopulated] = React.useState(false);

  useEffect(() => {
    if (populateFormWithSavedData && !hasPopulated) {
      populateFormWithSavedData();
      setHasPopulated(true);
    }
  }, [populateFormWithSavedData, hasPopulated]);

  if (!isFemale) {
    return null;
  }

  const handleNextClick = async () => {
    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      return;
    }

    try {
      if (saveStepData) {
        await saveStepData(3);
      }
    } catch {
      return;
    }

    setCurrentStep({ number: 4, name: 'Medical History' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='step-3'>
      <p>
        Members Testing for Female Sex Hormones - please answer these as
        accurately as possible
      </p>
      <DatePickerComp
        title='What was the date of your last menstrual period?'
        placeholder='Enter your last menstrual period'
        onDateChange={(date) => setFieldValue('last_period_date', date)}
        selectedDate={values.last_period_date}
        showTimeInput={false}
      />
      <TextInput
        title='Do you currently take hormonal contraception or HRT? If so, please provide the name and how long you have taken this for.'
        name='hormonal_contraception_hrt'
        placeholder='Enter hormonal contraception or HRT name'
        type='text'
        value={values.hormonal_contraception_hrt}
        onChange={(e) =>
          setFieldValue('hormonal_contraception_hrt', e.target.value)
        }
      />
      <TextInput
        title='Do you know your typical cycle length?'
        name='typical_cycle_length'
        placeholder='Enter typical cycle length'
        type='text'
        value={values.typical_cycle_length}
        onChange={(e) => setFieldValue('typical_cycle_length', e.target.value)}
      />
      <TextInput
        title='Primary reason for testing (e.g., PMS, fertility concerns, cycle mapping, fatigue)?'
        name='primary_testing_reason'
        placeholder='Enter primary reason for testing'
        type='text'
        value={values.primary_testing_reason}
        onChange={(e) =>
          setFieldValue('primary_testing_reason', e.target.value)
        }
      />
      <TextInput
        title='Menstrual flow characteristics (e.g., heavy, light, painful, presence of spotting)?'
        name='menstrual_flow_characteristics'
        placeholder='Enter menstrual flow characteristics'
        type='text'
        value={values.menstrual_flow_characteristics}
        onChange={(e) =>
          setFieldValue('menstrual_flow_characteristics', e.target.value)
        }
      />
      <TextInput
        title='If postmenopausal, age at last menstrual period or whether surgical menopause occurred?'
        name='menopause_details'
        placeholder='Enter here'
        type='text'
        value={values.menopause_details}
        onChange={(e) => setFieldValue('menopause_details', e.target.value)}
      />
      <TextInput
        title="Is there anything specific you'd like our clinical team to know before your assessment?"
        name='additional_notes_for_clinical_team'
        placeholder='Enter anything specific'
        type='text'
        value={values.additional_notes_for_clinical_team}
        onChange={(e) =>
          setFieldValue('additional_notes_for_clinical_team', e.target.value)
        }
      />

      <div className='submit-btn-wrapper'>
        <Button
          text='Back'
          onClick={() => {
            setCurrentStep({ number: 2, name: 'Current Lifestyle' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          backgroundColor={'#ffffff'}
          color={'#2D334E'}
        />
        <Button text='Next' isDisabled={false} onClick={handleNextClick} />
      </div>
    </div>
  );
};

export default Step5;
